import { Injectable } from '@angular/core';
import { EMPTY, Observable, catchError, expand, filter, map, reduce } from 'rxjs';

import { ConfigurationService } from '@sondermind/configuration';
import { Logger } from '@sondermind/datadog-logging';
import { HttpService } from '@sondermind/http';
import { IAPIListRequestOptions, IAPIListResponse } from '@sondermind/utilities/models-http';

import { camelCaseTransformer } from '@sondermind/utilities/tools';

import {
  Provider,
  IProviderApiResponse
} from '../models/provider.model';

import { IProviderPNCInfo, IProviderPNCAPIResponse } from '../models/provider-pnc-ids.model';

@Injectable({
  providedIn: 'root'
})
export class ProviderHttpService {
  constructor(
    private configurationService: ConfigurationService,
    private http: HttpService,
    private logger: Logger
  ) {}

  get indexUrl(): string {
    return `${this.configurationService.env.svcBase}/provider_profiles/api/v1/providers`;
  }

  /**
   * Fetches a specific provider by account UUID from the API
   * @param accountUuid The account UUID of the provider to fetch
   * @param opts Optional request options
   * @returns Observable of Provider
   */
  fetchByAccountUuid(opts: Partial<IAPIListRequestOptions> = {}): Observable<Provider> {
    const params = this.http.generateListParams(opts);

    return this.http
      .get<IAPIListResponse<IProviderApiResponse>>(this.indexUrl, params)
      .pipe(
        map((resp) => {
          if (!resp.data?.length) {
            throw new Error('Provider not found');
          }
          return new Provider(resp.data[0]);
        }),
        this.http.handleHttpErrorStrict()
      );
  }

  /**
   * Fetches all providers with automatic pagination
   * Gracefully handles pagination errors - returns partial data if later pages fail
   * @param opts Optional request options
   * @returns Observable of all Providers across all pages (or partial if pagination fails)
   */
  list$(opts: Partial<IAPIListRequestOptions>): Observable<Provider[]> {
    return this.fetchPage(opts).pipe(
      expand((response) => {
        if (!response.meta.links.next) {
          return EMPTY;
        }

        // Fetch next page with error handling to prevent pagination failures from breaking the entire stream
        return this.fetchPageByUrl(response.meta.links.next).pipe(
          catchError((error: unknown) => {
            this.logger.error('Pagination failed - returning data fetched so far', {
              failedUrl: response.meta.links.next,
              error
            });
            // Stop pagination by returning EMPTY, but allow the stream to complete with data collected so far
            return EMPTY;
          })
        );
      }),
      // Collect all pages and flatten the data arrays
      reduce((allProviders: Provider[], response) => allProviders.concat(response.data), [])
    );
  }

  /**
   * Fetches providers by persona provider IDs
   * @param providerPersonaIds Array of persona provider IDs
   * @param opts Optional request options
   * @returns Observable of Provider array
   */
  fetchByPersonaProviderIds$(
    providerPersonaIds: number[],
    opts: Partial<IAPIListRequestOptions> = {}
  ): Observable<Provider[]> {
    let params = this.http.generateListParams(opts);
    providerPersonaIds.forEach((id) => { params = params.append('filter[ids][]', id.toString()) });

    return this.http
      .get<IAPIListResponse<IProviderApiResponse>>(this.indexUrl, params)
      .pipe(
        map((resp) => resp.data.map((provider) => new Provider(provider))),
        this.http.handleHttpErrorStrict()
      );
  }

  getPNCInfo$(
    providerPersonaIds: number[],
    opts: Partial<IAPIListRequestOptions> = {}
  ): Observable<IProviderPNCInfo[]> {
    const apiUrl = `${this.indexUrl}/provider_network_companies`;
    let params = this.http.generateListParams(opts);
    providerPersonaIds.forEach((id) => { params = params.append('persona_provider_ids[]', id.toString()) });

    return this.http
      .get<IAPIListResponse<IProviderPNCAPIResponse[]>>(apiUrl, params)
      .pipe(
        map((jsonRes) => {
          const transformedData = camelCaseTransformer<IProviderPNCAPIResponse[]>(jsonRes.data);
          return transformedData.map((item) => ({
            personaProviderId: item.personaProviderId,
            pncIds: item.activeMatchingNetworks.map((network) => network.id)
          }));
        }),
        this.http.handleHttpErrorStrict()
      );
  }

  /**
   * Fetches a single page of providers
   * @param opts Optional request options
   * @returns Observable of IAPIListResponse with Providers
   */
  fetchPage(opts: Partial<IAPIListRequestOptions> = {}): Observable<IAPIListResponse<Provider>> {
    const params = this.http.generateListParams(opts);

    return this.http
      .get<IAPIListResponse<IProviderApiResponse>>(this.indexUrl, params)
      .pipe(
        map((resp) => ({
          ...resp,
          data: resp.data.map((provider) => new Provider(provider))
        })),
        this.http.handleHttpError(),
        filter((resp): resp is IAPIListResponse<Provider> => !!resp)
      );
  }

  /**
   * Fetches a page by direct URL (used for pagination)
   * @param url The full URL to fetch
   * @returns Observable of IAPIListResponse with Providers
   */
  private fetchPageByUrl(url: string): Observable<IAPIListResponse<Provider>> {
    return this.http
      .get<IAPIListResponse<IProviderApiResponse>>(url)
      .pipe(
        map((resp) => ({
          ...resp,
          data: resp.data.map((provider) => new Provider(provider))
        })),
        this.http.handleHttpError(),
        filter((resp): resp is IAPIListResponse<Provider> => !!resp)
      );
  }
}
